import { yupResolver } from '@hookform/resolvers/yup'
import cx from 'classnames'
import React, { forwardRef, ReactElement, useEffect, useRef } from 'react'
import {
  FieldError,
  FieldErrorsImpl,
  FieldValues,
  FormProvider,
  useForm,
  useFormContext,
  UseFormReturn,
  ValidationMode
} from 'react-hook-form'
import { DefaultValues } from 'react-hook-form/dist/types/form'

import { TRACKING_MAPPING } from '@js/common/constants'
import { WTValue } from '@js/WTPayload'

import { Maybe } from '@f-fertighaus/applications/Next/types/utility'
import { webtrack } from '@f-fertighaus/applications/Next/utils/webtrack'
import { $TSFixMe } from '@f-fertighaus/applications/UserAccount/interfaces/utils'

import yup from './validator/yup'

export interface FormFieldError extends FieldError {
  name: string
}

interface FormWrapperProps<FormData extends object> {
  children: React.ReactNode
  onSubmit: (values: FormData) => void | Promise<void>
  defaultValues: DefaultValues<FormData>
  wtData?: $TSFixMe
  className?: string
  validationSchema: yup.ObjectSchema<FormData>
  wtEnabled?: boolean
  mode?: keyof ValidationMode
  formId?: string
  initialErrors?: FormFieldError[]
}

function BaseFormWrapper<FormData extends object>(
  {
    defaultValues,
    onSubmit,
    className,
    validationSchema,
    children,
    wtData,
    mode = 'onBlur',
    wtEnabled = true,
    formId,
    initialErrors
  }: FormWrapperProps<FormData>,
  ref?: React.Ref<HTMLFormElement>
) {
  const isInitialLoad = useRef(true)
  const methods = useForm({
    defaultValues: defaultValues,
    resolver: yupResolver(validationSchema as yup.ObjectSchema<FormData>),
    mode: mode
  })

  const onSubmitSuccess = async (values: { [key: string]: string }) => {
    if (wtEnabled) {
      webtrack({
        category: 'form',
        action: 'submit',
        values: Object.entries(values).map(([key, value]) => ({
          type: TRACKING_MAPPING[key]?.label || key,
          value: TRACKING_MAPPING[key]?.isTrackingAllowed
            ? value
            : 'VALUE-HIDDEN'
        })),
        ...(wtData.formSubmit || wtData.form || {})
      })
    }

    return onSubmit(values as FormData)
  }

  const onSubmitError = (errors: Partial<FieldErrorsImpl<$TSFixMe>>) => {
    if (!wtEnabled) {
      return
    }

    window._wt({
      category: 'form',
      action: 'fail',
      values: Object.entries(errors).map(([key, value]) => ({
        type:
          TRACKING_MAPPING[key] && TRACKING_MAPPING[key].label
            ? TRACKING_MAPPING[key].label
            : key,
        value: value?.type === 'required' ? 'not-empty' : value?.type || ''
      })) as WTValue[],
      flow_information: wtData.form?.flow_information
    })
  }

  useEffect(() => {
    if (isInitialLoad.current) {
      initialErrors?.forEach(({ name, ...error }) => {
        methods.setError(name, { ...error })
      })
    } else {
      isInitialLoad.current = false
    }
  }, [initialErrors, methods])

  return (
    <FormProvider {...methods}>
      <form
        ref={ref}
        id={formId}
        className={cx(className)}
        onSubmit={methods.handleSubmit(onSubmitSuccess, onSubmitError)}>
        {children}
      </form>
    </FormProvider>
  )
}

interface FormWrapperComponent {
  <FormData extends object>(
    props: FormWrapperProps<FormData> & {
      ref?: React.Ref<Maybe<HTMLFormElement>>
    }
  ): ReactElement
}

export const FormWrapper = forwardRef(BaseFormWrapper) as FormWrapperComponent

interface ConnectFormProps<TFieldValues extends FieldValues> {
  children(children: UseFormReturn<TFieldValues>): ReactElement
}
export const ConnectForm = <TFieldValues extends FieldValues>({
  children
}: ConnectFormProps<TFieldValues>) => {
  const methods = useFormContext<TFieldValues>()

  return children({ ...methods })
}
